import { getRootDomain } from "../../../utils";
import { getAnalyticsState } from "../stateAccess";
import { AnalyticsEvent, AnalyticsIdentity, AnalyticsInitConfig, AnalyticsProperties, AnalyticsProvider } from "../types";

type PendingOperation = () => void;

const queue: PendingOperation[] = [];
let initialized = false;
let loading = false;
let disabled = false;

const INTERNAL_EMAIL_DOMAINS = ["@loginradius.com"];

const INTERCOM_SETTLE_POLL_MS = 250;
const INTERCOM_SETTLE_MAX_WAIT_MS = 5000;

// Takes the email as a parameter (rather than reading analytics state itself) so it stays a
// pure function callers can unit test directly, e.g. isInternalUser("user@loginradius.com").
const isInternalUser = (email?: string) => typeof email === "string" && INTERNAL_EMAIL_DOMAINS.some((domain) => email.toLowerCase().endsWith(domain));

const getIntercomEmail = () => getAnalyticsState()?.auth?.intercomUserData?.response?.email;

const isIntercomDataSettled = () => {
  if (localStorage.getItem("isLoggedIn") !== "true") {
    return true;
  }
  const intercomUserData = getAnalyticsState()?.auth?.intercomUserData;
  return Boolean(intercomUserData?.isSuccess || intercomUserData?.isError);
};

const flush = () => {
  while (queue.length) {
    const operation = queue.shift();
    operation?.();
  }
};

const runWhenReady = (operation: PendingOperation) => {
  if (disabled) {
    return;
  }

  if (!initialized || !window.amplitude) {
    queue.push(operation);
    return;
  }

  operation();
};

const maybeLoadSessionReplay = (elapsedMs = 0) => {
  if (import.meta.env.VITE_ENABLE_SESSION_REPLAY !== "true" || window.sessionReplayOn) {
    return;
  }

  if (!isIntercomDataSettled() && elapsedMs < INTERCOM_SETTLE_MAX_WAIT_MS) {
    setTimeout(() => maybeLoadSessionReplay(elapsedMs + INTERCOM_SETTLE_POLL_MS), INTERCOM_SETTLE_POLL_MS);
    return;
  }

  if (isInternalUser(getIntercomEmail())) {
    return;
  }

  const sessionReplayImport = document.createElement("script");
  sessionReplayImport.type = "text/javascript";
  sessionReplayImport.async = true;
  sessionReplayImport.src = "https://cdn.amplitude.com/libs/plugin-session-replay-browser-1.19.3-min.js.gz";
  sessionReplayImport.onload = () => {
    if (window.sessionReplay && window.amplitude && !isInternalUser(getIntercomEmail())) {
      const sessionReplayTracking = window.sessionReplay.plugin({
        sampleRate: 1,
        sessionId: window.amplitude.getSessionId()
      });
      window.amplitude.add(sessionReplayTracking);
    }
  };
  const script = document.getElementsByTagName("script")[0];
  script?.parentNode?.insertBefore(sessionReplayImport, script);
  window.sessionReplayOn = true;
};

const loadAmplitudePlugins = (appKey: string) => {
  maybeLoadSessionReplay();

  const engagementImport = document.createElement("script");
  engagementImport.type = "text/javascript";
  engagementImport.async = true;
  engagementImport.src = `https://cdn.amplitude.com/script/${appKey}.engagement.js`;
  engagementImport.onload = () => {
    if (window.engagement && window.amplitude?.add) {
      window.amplitude.add(window.engagement.plugin());
    }
  };
  const script = document.getElementsByTagName("script")[0];
  script?.parentNode?.insertBefore(engagementImport, script);
};

const initializeSdk = (appKey: string) => {
  window.amplitude.init(appKey, undefined, {
    cookieOptions: {
      domain: getRootDomain(),
      expiration: 365,
      secure: true,
      sameSite: "Lax"
    },
    identityStorage: "cookie",
    fetchRemoteConfig: true,
    autocapture: true,
    flushQueueSize: 10,
    flushIntervalMillis: 10000,
    deviceIdFromUrlParam: true,
    includeUtm: true
  });
  initialized = true;
  loadAmplitudePlugins(appKey);
  flush();
};

export const amplitudeProvider: AnalyticsProvider = {
  name: "amplitude",
  init(config: AnalyticsInitConfig) {
    const appKey = config.amplitudeAppKey;
    if (!appKey || initialized || loading) {
      disabled = !appKey && !initialized;
      return;
    }

    disabled = false;
    loading = true;

    if (window.amplitude) {
      initializeSdk(appKey);
      loading = false;
      return;
    }

    const amplitudeImport = document.createElement("script");
    amplitudeImport.type = "text/javascript";
    amplitudeImport.async = true;
    amplitudeImport.src = `https://cdn.amplitude.com/script/${appKey}.js`;
    amplitudeImport.onload = () => {
      if (window.amplitude) {
        initializeSdk(appKey);
      }
      loading = false;
    };
    amplitudeImport.onerror = () => {
      loading = false;
    };

    const script = document.getElementsByTagName("script")[0];
    script?.parentNode?.insertBefore(amplitudeImport, script);
  },
  track(event: AnalyticsEvent) {
    runWhenReady(() => window.amplitude?.track(event.name, event.properties || {}));
  },
  identify(identity: AnalyticsIdentity) {
    runWhenReady(() => {
      if (identity.userId && !window.amplitude?.getUserId?.()) {
        window.amplitude.setUserId(identity.userId);
      }
      if (identity.traits && window.amplitude?.Identify) {
        const identifyEvent = new window.amplitude.Identify();
        Object.entries(identity.traits).forEach(([key, value]) => identifyEvent.set(key, value));
        window.amplitude.identify(identifyEvent);
      }
    });
  },
  setUserProperties(properties: AnalyticsProperties) {
    runWhenReady(() => {
      if (!window.amplitude?.Identify) {
        return;
      }
      const identifyEvent = new window.amplitude.Identify();
      Object.entries(properties).forEach(([key, value]) => identifyEvent.set(key, value));
      window.amplitude.identify(identifyEvent);
    });
  },
  reset() {
    runWhenReady(() => window.amplitude?.reset?.());
  }
};
